import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { uuid } from 'uuidv4';

import PortfolioSection from '../Section/PortfolioSection';
import RecentTransactionCard from './RecentTransactionsCard';
import TransactionDetails from '../../Modals/Transaction/TransactionDetails';
import Modal from '../../Modal/Modal';
import SellFromHomeTransactionDetails from '../../Modals/SellWizard/SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from '../../Modals/SellWizard/SellFromHomeTransactionHistory';
import SfhModalTitle from '../../Modals/SellWizard/SfhModalTitle';
import {
  emptyPortfolioTransactionSingle,
  portfolioTransactionsSingleFetch,
} from '../../../store/actions/portfolio/portfolioActions';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { formatDate } from '../../../util/helpers/dateHelpers';
import { transactionFieldFormatter } from '../../../util/helpers/transcation/transactionHelper';
import BlockSectionLoader from '../../Loader/BlockSectionLoader';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import SectionLoader from '../../Loader/SectionLoader';
import { EmptyTable } from '../../EmptyTable/EmptyTable';
import { uFlexColumn } from '../../../assets/styles/utility';
import { variables } from '../../../assets/styles/variables';
import PendingTransactionCard from './PendingTransactionCard';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import {
  transactionRequiestStatusTypesArray,
  transactionTypes,
  SELL_FROM_HOME,
} from '../../../util/enum/api/transactionTypes';
import CancelPendingTransactionModal from '../../Modals/CancelPendingTransaction/CancelPendingTransactionModal';
import { pxToRem } from '../../../assets/styles/helper';

const RecentTransaction = styled.div`
  ${uFlexColumn};
  box-shadow: ${variables.shadow.boxShadow};
  border-radius: ${variables.borderRadius.borderRadius};
`;

const RecentTransactions = ({
  data,
  pendingTransactions,
  error,
  accountUid,
  isLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelTransactionData, setCancelTransactionData] = useState({});
  const [sfhTransactionUid, setSfhTransactionUid] = useState(null);
  const [isSfhHistoryView, setIsSfhHistoryView] = useState(false);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  const onTransactionCardClick = useCallback(
    (transaction) => {
      // Sell From Home rows open the dedicated SFH details modal, which fetches
      // the single transaction itself from the Uid it is handed. The recent
      // transactions feed is unmapped, so Type arrives as the raw number (39);
      // also accept the mapped string in case a mapped feed is used.
      if (
        transaction.Type === SELL_FROM_HOME ||
        transaction.Type === transactionTypes.SellFromHome
      ) {
        setSfhTransactionUid(transaction.Uid);
        return;
      }
      dispatch(
        portfolioTransactionsSingleFetch({
          accountUid,
          transactionUid: transaction.Uid,
        }),
      );
      setIsTransactionDetailsOpen(true);
    },
    [accountUid, dispatch],
  );

  const handleSfhDetailsClose = useCallback(() => {
    dispatch(emptyPortfolioTransactionSingle());
    setSfhTransactionUid(null);
    setIsSfhHistoryView(false);
  }, [dispatch]);

  const handleSfhViewHistory = useCallback(() => setIsSfhHistoryView(true), []);
  const handleSfhHistoryBack = useCallback(
    () => setIsSfhHistoryView(false),
    [],
  );

  const handleCancelModalOpen = (accountUid, paymentUid) => {
    setIsCancelModalOpen(true);
    setCancelTransactionData({
      accountUid,
      paymentUid,
    });
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
    setCancelTransactionData({});
  };

  const renderTransactions = useCallback(() => {
    const renderBody = () => {
      const transactions = [];
      if (!isEmpty(data) && !!data.length) {
        transactions.push(...data);
      }
      if (!isEmpty(pendingTransactions) && !!pendingTransactions.length) {
        transactions.push(
          ...pendingTransactions?.map((transaction) => ({
            ...transaction,
            DateTime: transaction.RequestDate,
          })),
        );
      }
      const sortedTransactions = transactions.sort((a, b) => {
        const dateA = new Date(a.DateTime);
        const dateB = new Date(b.DateTime);
        return dateB - dateA;
      });
      return !isEmpty(sortedTransactions) && !!sortedTransactions.length
        ? sortedTransactions.map((transaction) =>
            transaction.DepositKey ? (
              <PendingTransactionCard
                key={`${transaction.DepositKey}-${uuid()}`}
                month={formatDate(transaction.DateTime, 'MMMM')}
                day={formatDate(transaction.DateTime, 'd')}
                amount={transaction.Amount}
                status={parseEnumType(
                  transactionRequiestStatusTypesArray,
                  transaction.RequestStatus,
                )}
                canCancel={transaction.CanCancel}
                action={transaction.TransactionType}
                accountUid={accountUid}
                paymentUid={transaction.DepositKey}
                handleModalOpen={handleCancelModalOpen}
              />
            ) : (
              <RecentTransactionCard
                key={`${transaction.Uid}_${transaction.DateTime}`}
                month={formatDate(transaction.DateTime, 'MMMM')}
                day={formatDate(transaction.DateTime, 'd')}
                action={
                  transaction.Fields[0]
                    ? transactionFieldFormatter(transaction.Fields[0])
                    : null
                }
                actionSubject={
                  transaction.Fields[1]
                    ? transactionFieldFormatter(transaction.Fields[1])
                    : null
                }
                isSegregated={transaction.IsSegregated}
                status={transaction.StatusType}
                onClick={() => onTransactionCardClick(transaction)}
              />
            ),
          )
        : null;
    };

    return (
      <>
        {isLoading ? (
          <BlockSectionLoader isLoading={isLoading} />
        ) : (
          <>
            {renderBody()}
            {(isEmpty(data) || !data.length) &&
              (isEmpty(pendingTransactions) || !pendingTransactions.length) && (
                <EmptyTable>
                  <p>{t('portfolio.transactions.tableEmpty')}</p>
                </EmptyTable>
              )}
          </>
        )}
      </>
    );
  }, [
    data,
    pendingTransactions,
    accountUid,
    isLoading,
    onTransactionCardClick,
    t,
  ]);

  return (
    <PortfolioSection title="Recent Transactions">
      <RecentTransaction>
        {!error ? (
          <SectionLoader isLoading={singleTransactionLoading}>
            {renderTransactions()}
          </SectionLoader>
        ) : (
          <span>{error}</span>
        )}
      </RecentTransaction>
      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
      />
      <CancelPendingTransactionModal
        handleClose={handleCancelModalClose}
        isOpen={isCancelModalOpen}
        cancelTransactionData={cancelTransactionData}
      />
      <Modal
        isOpen={Boolean(sfhTransactionUid)}
        close={handleSfhDetailsClose}
        goBack={isSfhHistoryView ? handleSfhHistoryBack : undefined}
        title={
          <SfhModalTitle>
            {t(
              isSfhHistoryView
                ? 'sellWizard.sfhWizard.transactionHistory.title'
                : 'sellWizard.sfhWizard.transactionDetails.title',
            )}
          </SfhModalTitle>
        }
        headerBordered
        size="sm"
        maxWidth={pxToRem(640)}
        minHeight={pxToRem(640)}
        height={pxToRem(640)}
        clipContent
        closeIconVisible
        skipListeningToLocations
        dataCy="container-sfh-transaction-details"
      >
        {sfhTransactionUid &&
          (isSfhHistoryView ? (
            <SellFromHomeTransactionHistory
              lockedSnapshot={{ transactionUid: sfhTransactionUid }}
              handleClose={handleSfhDetailsClose}
            />
          ) : (
            <SellFromHomeTransactionDetails
              lockedSnapshot={{ transactionUid: sfhTransactionUid }}
              handleClose={handleSfhDetailsClose}
              handleViewHistory={handleSfhViewHistory}
              source="Recent Transactions"
            />
          ))}
      </Modal>
    </PortfolioSection>
  );
};

RecentTransactions.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  pendingTransactions: PropTypes.arrayOf(PropTypes.shape({})),
  error: PropTypes.string,
  accountUid: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default RecentTransactions;
